import { useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { CANCEL_MY_APPOINTMENT_REQUEST_MUTATION, MY_APPOINTMENTS_QUERY } from './portal.gql';

import { Button } from '@/shared/ui/button';

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument */

const CONFIRMED_STATUSES = new Set(['confirmed', 'checked_in', 'in_progress']);

export function PortalAppointmentsPage(): JSX.Element {
  const { t } = useTranslation();
  const { data, loading, error, refetch } = useQuery(MY_APPOINTMENTS_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  });
  const appts: any[] = data?.myAppointments ?? [];
  const [cancelRequest, { loading: cancelling }] = useMutation(CANCEL_MY_APPOINTMENT_REQUEST_MUTATION);
  const [cancel_error, setCancelError] = useState(false);

  const onCancelRequest = async (id: string): Promise<void> => {
    if (!window.confirm(t('portal.cancel_request_confirm'))) return;
    setCancelError(false);
    try {
      await cancelRequest({ variables: { appointment_id: id } });
    } catch {
      setCancelError(true);
    }
    await refetch();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{t('portal.nav_appointments')}</h2>
        <Button asChild size="sm">
          <Link to="/portal/appointments/new">{t('portal.book_cta')}</Link>
        </Button>
      </div>
      {cancel_error ? <p className="text-destructive text-sm">{t('portal.cancel_request_error')}</p> : null}
      {loading && appts.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
      ) : error && appts.length === 0 ? (
        <p className="text-destructive text-sm">{t('portal.load_error')}</p>
      ) : appts.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('portal.no_appointments')}</p>
      ) : (
        <ul className="space-y-2">
          {appts.map((a) => {
            const is_confirmed = CONFIRMED_STATUSES.has(a.status);
            return (
              <li key={a.id} className="bg-background rounded-xl border p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">
                    {new Date(a.scheduled_start_at).toLocaleString([], {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </span>
                  <span
                    className={`rounded px-2 py-0.5 text-xs ${
                      a.status === 'requested'
                        ? 'bg-amber-100 text-amber-800'
                        : is_confirmed
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'text-muted-foreground bg-muted'
                    }`}
                  >
                    {t(`portal.appt_status_${a.status}`, { defaultValue: a.status })}
                  </span>
                </div>
                <p className="text-muted-foreground text-xs">
                  {t(`portal.appt_kind_${a.appointment_kind}`, { defaultValue: a.appointment_kind })}
                  {a.veterinarian_name ? ` · ${a.veterinarian_name}` : ''}
                </p>
                {is_confirmed && a.consulting_room_name ? (
                  <p className="text-xs font-medium text-emerald-700">
                    {t('portal.consulting_room_label')}: {a.consulting_room_name}
                  </p>
                ) : null}
                {a.status === 'requested' ? (
                  <div className="mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={cancelling}
                      onClick={() => void onCancelRequest(a.id)}
                    >
                      {t('portal.cancel_request_cta')}
                    </Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
